import { useMemo } from "react";

import { type Entitlements, Story, entitlementsFor } from "@baiki/interfaces";

import { useAuth, useCustomer } from "@/providers";

const useEntitlements = (): Entitlements => {
  const { customer } = useCustomer();
  return useMemo(() => entitlementsFor(customer ?? null), [customer]);
};

const useFeatureFlags = () => {
  const { user } = useAuth();
  const entitlements = useEntitlements();

  const isDeveloper =
    import.meta.env.DEV || import.meta.env.VITE_APP_ENV === "development";

  return useMemo(
    () => ({
      canCompose: entitlements.canCompose,
      canGenerateAudio: entitlements.isGod || isDeveloper,
      canPickTrackToPlay: entitlements.isGod || isDeveloper,
      canSeeAllStoriesFromAllUsers: isDeveloper || entitlements.isGod,
      canRefundStory: entitlements.isGod || isDeveloper,
      canRealignStory: entitlements.isGod || isDeveloper,
      canSeeBetaLanguages: entitlements.isGod || isDeveloper,
      canSetStoriesFeatured: entitlements.isGod || isDeveloper,
      // Sub-gated capabilities sourced from the shared `Entitlements`
      // record. Sharing also requires the user to own the story.
      canShareStory: (story?: Story) => {
        if (story?.authorId !== user?.uid) return false;
        return entitlements.canShareStory;
      },
      // A translation costs the same 5 sparks as a story and rides the same
      // entitlement — there is deliberately no separate flag for it.
      canTranslateStory: (story?: Story) => {
        if (story?.authorId !== user?.uid) return false;
        return entitlements.canCompose;
      },
      canBrowseFullCatalog: entitlements.canBrowseFullCatalog,
      canPrintStory: entitlements.canPrintStory,
      canCreatePlaylists: entitlements.canCreatePlaylists,
      isGod: entitlements.isGod,
    }),
    [entitlements, isDeveloper, user?.uid],
  );
};

const useStoriesPageFlags = () => {
  const entitlements = useEntitlements();

  return useMemo(
    () => ({
      canBrowseFullCatalog: entitlements.canBrowseFullCatalog,
    }),
    [entitlements],
  );
};

export const FeaturesHooks = {
  useFeatureFlags,
  useStoriesPageFlags,
  useEntitlements,
};
